import { Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import {
  federalWasteGeneratorStatusToLabel,
  stateWasteGeneratorStatusToLabel,
  wasteGeneratorStatusToLongLabel,
} from "encamp-shared/src/waste";
import { stateDoesNotFollowFederalGeneratorStatus } from "encamp-shared/src/waste/constants";
import { RcraInfoFederalWasteGeneratorStatus } from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import pluralize from "pluralize";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { prettyPrintMonthYear } from "util/dates";

import { CALCULATED_STATUS_EXPLANATION } from "../calculatedStatusCopy";
import { EpisodicExemptFootnotes } from "./EpisodicExemptFootnotes";
import { ExpandableInfoContainerCombined } from "./ExpandableInfoSection";
import { GreySimpleGrid } from "./GreySimpleGrid";
import { StatusComparisonSection } from "./StatusComparisonSection";
import { useGenerationColumn } from "./useGenerationColumn";
import { useGeneratorStatusSummary } from "./useGeneratorStatusSummary";
import { useHasWasteShipmentData } from "./useHasWasteShipmentData";
import { useWasteFacility } from "./useWasteFacility";
import { useWasteFacilityState } from "./WasteFacilityContext";

const updateNotificationTooltip =
  "If the facility's calculated generator statuses differ from the EPA current status, you may need to file an update notification with the EPA.";

const currentStatusTooltip =
  "Federal waste generator status as currently listed with the EPA, for both federal and state, if applicable.\
  \n\nReported data can take up to 2 weeks to update.\n\n" + updateNotificationTooltip;

const epaStatusChangesTooltip =
  "The generator statuses listed with the EPA, if they change month to month.";

/** Composed into every calculated-status tooltip below, so the rule is stated once. */
const calculatedStatusesTooltip =
  `${CALCULATED_STATUS_EXPLANATION} ` +
  "Calculated statuses update shortly after new waste data is added or changed.";

const potentialExceedancesTooltip =
  "Any months where our calculated generator status exceeds the EPA generator status.\n\n" +
  calculatedStatusesTooltip;

const belowThresholdTooltip =
  "Any months where our calculated generator status is below the EPA generator status.\n\n" +
  calculatedStatusesTooltip;

const stateExceedancesTooltip =
  "Months where our calculated STATE generator status exceeds the state status " +
  "reported to the EPA — independent of the federal comparison. Shown for states " +
  "whose generator categories differ from the federal rules.\n\n" +
  calculatedStatusesTooltip;

const stateBelowThresholdTooltip =
  "Months where our calculated STATE generator status is below the state status " +
  "reported to the EPA.\n\n" +
  calculatedStatusesTooltip;

const governingStatusTooltip =
  "The generator status that sets this facility's on-site accumulation limit and central-accumulation " +
  "deadlines. It starts from the status reported to the EPA, or the highest calculated status when " +
  "nothing is reported, and container fill events can only raise it. When it sits above the EPA " +
  "status, the facility's containers are already held to the shorter deadlines and lower limit of the " +
  "higher tier.\n\n" +
  updateNotificationTooltip;

/**
 * The governing status (`generationDrivenStatus`), shown under the reported status
 * rather than replacing it — both are true and they legitimately differ. It isn't
 * labeled as coming from containers: whichever source is highest wins, and
 * containers often aren't it.
 */
const GoverningStatusLine = ({
  containerTrackingEnabled,
  governingStatus,
  reportedFederalStatus,
}: {
  containerTrackingEnabled: boolean;
  governingStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined;
  reportedFederalStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined;
}) => {
  const theme = useTheme();
  if (!containerTrackingEnabled) return null;

  return (
    <Stack gap={0.25} sx={{ pt: 0.75 }}>
      <Stack direction="row" alignItems="center" gap={0.25}>
        <Typography fontSize={11} color="textSecondary">
          Governing status
        </Typography>
        <InfoIconTooltip
          tooltip={governingStatusTooltip}
          iconButtonSx={{ color: theme.palette.grey[400] }}
        />
      </Stack>
      {!governingStatus && (
        <Typography variant="body2" fontSize={"0.875rem"} color="text.disabled">
          Could not be determined
        </Typography>
      )}
      {governingStatus && (
        <Stack direction="row" spacing={1}>
          <Typography variant="body2" fontSize={"0.875rem"}>
            {wasteGeneratorStatusToLongLabel(governingStatus)}
          </Typography>
          <Typography
            fontStyle="italic"
            color="text.disabled"
            variant="caption"
            fontSize={"0.75rem"}
          >
            Federal
          </Typography>
        </Stack>
      )}
      {reportedFederalStatus && governingStatus && reportedFederalStatus !== governingStatus && (
        <Typography variant="caption" color="text.secondary" fontSize={"0.75rem"}>
          Above reported status — this facility's containers are on the shorter accumulation
          deadlines and lower on-site limit.
        </Typography>
      )}
    </Stack>
  );
};

export const GeneratorStatusTile = () => {
  return (
    <DashboardTile size={{ sm: 12, md: 5 }}>
      <GeneratorStatusTileContent />
    </DashboardTile>
  );
};

const GeneratorStatusTileContent = () => {
  const { facilityId, tenantId } = useParams<{ facilityId: string; tenantId: string }>();
  const { data: wasteFacilityData } = useWasteFacility(facilityId);
  const {
    wasteFacilityState: { optimisticFilters, deferredFilters },
    effectiveWasteFacilityId,
  } = useWasteFacilityState();

  const { data: generatorStatusData } = useGeneratorStatusSummary(
    effectiveWasteFacilityId,
    deferredFilters.startDate ? new Date(deferredFilters.startDate) : null,
    deferredFilters.endDate ? new Date(deferredFilters.endDate) : null
  );

  const hasStartAndEndDate = Boolean(
    (optimisticFilters.startDate && optimisticFilters.endDate) ||
    (deferredFilters.startDate && deferredFilters.endDate)
  );

  const {
    currentlyReportedStatus,
    statusChanges,
    exceedances,
    belowThreshold,
    stateExceedances,
    stateBelowThreshold,
  } = generatorStatusData?.generatorStatusSummary ?? {};

  const facility = wasteFacilityData?.facility;
  const facilityState = facility?.state;
  const showState = stateDoesNotFollowFederalGeneratorStatus(facilityState);

  const currentlyReportedStatusArray = useMemo(() => {
    const res = [];
    if (currentlyReportedStatus?.fedWasteGenerator) {
      res.push({
        status: currentlyReportedStatus.fedWasteGenerator.status,
        date: currentlyReportedStatus.fedWasteGenerator.createdDate,
        type: "Federal",
      });
    }
    if (
      currentlyReportedStatus?.stateWasteGenerator &&
      stateDoesNotFollowFederalGeneratorStatus(wasteFacilityData?.facility?.state)
    ) {
      res.push({
        status: currentlyReportedStatus.stateWasteGenerator.status,
        date: currentlyReportedStatus.stateWasteGenerator.createdDate,
        type: `${wasteFacilityData?.facility?.state} State`,
      });
    }
    return res;
  }, [currentlyReportedStatus, wasteFacilityData]);

  // Check if there's any shipment-based data to determine if we should show calculated sections
  const hasShipmentData = useHasWasteShipmentData();

  const { featureFlags } = useFeatureFlags();
  const containerTrackingEnabled = Boolean(featureFlags[FeatureFlagKey.ContainerTracking]);
  const generation = useGenerationColumn({
    facilityId: effectiveWasteFacilityId,
    startDate: deferredFilters.startDate,
    endDate: deferredFilters.endDate,
    containerTrackingEnabled,
  });

  // The calculated sections have historically been gated on shipment data. A
  // pure container-tracking facility has generation rows but no shipments, so
  // without this it would show no calculated sections at all.
  const showCalculatedSections =
    hasShipmentData || (containerTrackingEnabled && generation.hasGenerationData);

  const theme = useTheme();

  const isLoading = !hasStartAndEndDate || !generatorStatusData;

  return (
    <>
      <DashboardTile.Header title="Generator Status" />
      <DashboardTile.ContentArea>
        <Stack spacing={2}>
          {isLoading && (
            <Stack gap={2}>
              <Skeleton height={60} variant="rounded" />
              <Skeleton height={60} variant="rounded" />
              <Skeleton height={60} variant="rounded" />
              <Skeleton height={60} variant="rounded" />
            </Stack>
          )}
          {/* Currently Reported Status - Not expandable */}
          {generatorStatusData && (
            <Stack
              sx={{
                backgroundColor: theme.palette.grey50.main,
                px: 2,
                py: 0.5,
                borderRadius: 1,
                gap: 0.25,
              }}
            >
              <Stack direction="row" alignItems="center" gap={0.25}>
                <Typography fontSize={11} color="textSecondary">
                  {currentlyReportedStatusArray.length > 0
                    ? `Current reported status as of ${prettyPrintMonthYear(
                        currentlyReportedStatusArray[0].date
                      )}`
                    : "No reported generator status available in RCRAInfo (EPA)"}
                </Typography>
                <InfoIconTooltip
                  tooltip={currentStatusTooltip}
                  iconButtonSx={{ color: theme.palette.grey[400] }}
                />
              </Stack>
              <Stack gap={0.25}>
                {currentlyReportedStatusArray.map((item) => (
                  <Stack direction="row" spacing={1} key={item.type}>
                    <Typography variant="body2" fontSize={"0.875rem"}>
                      {item.status && wasteGeneratorStatusToLongLabel(item.status)}
                    </Typography>
                    <Typography
                      fontStyle="italic"
                      color="text.disabled"
                      variant="caption"
                      fontSize={"0.75rem"}
                    >
                      {item.type}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              <GoverningStatusLine
                containerTrackingEnabled={containerTrackingEnabled}
                governingStatus={facility?.generationDrivenStatus}
                reportedFederalStatus={currentlyReportedStatus?.fedWasteGenerator?.status}
              />
            </Stack>
          )}

          {/* Show the calculated sections when there's shipment data, or when the
              tenant is on container tracking (see showCalculatedSections). */}
          {showCalculatedSections && (
            <>
              {/* Reported Status Changes - Expandable */}
              {statusChanges && statusChanges.changes.length > 0 && (
                <ExpandableInfoContainerCombined
                  label="Reported status history"
                  text={`${statusChanges.changeCount} ${pluralize(
                    "change",
                    statusChanges.changeCount
                  )} this period`}
                  disabled={!statusChanges.changes.length}
                  infoTooltip={epaStatusChangesTooltip}
                >
                  <GreySimpleGrid
                    getRowId={(row) => row.month}
                    columns={[
                      {
                        field: "month",
                        headerName: "Month",
                        flex: 1,
                        valueFormatter: (value) => prettyPrintMonthYear(value),
                      },
                      {
                        field: "reportedFederalGeneratorStatus",
                        headerName: "Federal Status",
                        flex: 1,
                        valueFormatter: (value) => federalWasteGeneratorStatusToLabel(value),
                      },
                      {
                        field: "reportedStateGeneratorStatus",
                        headerName: "State Status",
                        flex: 1,
                        valueFormatter: (value) => stateWasteGeneratorStatusToLabel(value),
                      },
                    ]}
                    rows={statusChanges.changes}
                  />
                </ExpandableInfoContainerCombined>
              )}
              {/* Federal and state month-by-month comparisons. */}
              {exceedances && exceedances.length > 0 && (
                <StatusComparisonSection
                  showWarningChip
                  label="Above reported status (federal)"
                  infoTooltip={potentialExceedancesTooltip}
                  text={`${exceedances.length} ${pluralize(
                    "month",
                    exceedances.length
                  )} above reported status this period`}
                  axis="federal"
                  rows={generation.withGenerationKg(exceedances)}
                  extraColumns={generation.columns}
                >
                  <EpisodicExemptFootnotes
                    rows={exceedances}
                    tenantId={tenantId}
                    facilityId={effectiveWasteFacilityId}
                  />
                </StatusComparisonSection>
              )}
              {belowThreshold && belowThreshold.length > 0 && (
                <StatusComparisonSection
                  label="Below reported status (federal)"
                  infoTooltip={belowThresholdTooltip}
                  text={`${belowThreshold.length} ${pluralize(
                    "month",
                    belowThreshold.length
                  )} this period`}
                  axis="federal"
                  rows={generation.withGenerationKg(belowThreshold)}
                  extraColumns={generation.columns}
                >
                  <EpisodicExemptFootnotes
                    rows={belowThreshold}
                    tenantId={tenantId}
                    facilityId={effectiveWasteFacilityId}
                  />
                </StatusComparisonSection>
              )}
              {/* State comparisons apply only to states with their own generator categories. */}
              {showState && stateExceedances && stateExceedances.length > 0 && (
                <StatusComparisonSection
                  showWarningChip
                  label={`Above reported status (${facilityState} state)`}
                  infoTooltip={stateExceedancesTooltip}
                  text={`${stateExceedances.length} ${pluralize(
                    "month",
                    stateExceedances.length
                  )} above reported status this period`}
                  axis="state"
                  rows={stateExceedances}
                />
              )}
              {showState && stateBelowThreshold && stateBelowThreshold.length > 0 && (
                <StatusComparisonSection
                  label={`Below reported status (${facilityState} state)`}
                  infoTooltip={stateBelowThresholdTooltip}
                  text={`${stateBelowThreshold.length} ${pluralize(
                    "month",
                    stateBelowThreshold.length
                  )} this period`}
                  axis="state"
                  rows={stateBelowThreshold}
                />
              )}
            </>
          )}
        </Stack>
      </DashboardTile.ContentArea>
    </>
  );
};
